"use client"
import useMergedRef from "@react-hook/merged-ref"
import React, {
  ComponentPropsWithoutRef,
  forwardRef,
  useMemo,
  useRef,
} from "react"
import { VariantProps } from "tailwind-variants"
import { Close } from "../../icons/material-symbols/outlined/Close"
import { InputVariantProps, inputVariants } from "../../styles"
import { classNames, tv } from "../../utils"
import { Flex, FlexProps } from "../Flex"
import { FlexCenter } from "../FlexCenter"
import { Separator } from "../Separator"
import { Slot, SlotProps } from "../Slot"
import { UnstyledButton } from "../UnstyledButton"
import { VerticalAligned } from "../VerticalAligned"

export type InputProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "onClick" | "size"
> & {
  /**
   * An input helper rendered before and attached to the input field.
   */
  startEnhancer?: React.ReactNode

  /**
   * An input helper rendered after and attached to the input field.
   */
  endEnhancer?: React.ReactNode

  /**
   * Whether to render an X icon at the end of the input field that can clear the Input.
   */
  clearable?: boolean

  /**
   * Whether to clear the input value when the escape key is pressed.
   */
  clearOnEscape?: boolean
  inputRef?:
    | React.RefObject<HTMLInputElement>
    | ((instance: HTMLInputElement) => void)
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onEnter?: (value: InputProps["value"]) => unknown
  overrides?: {
    Input?: Partial<Omit<InputProps, "size">>
    Container?: Partial<FlexProps>
  }
  warning?: boolean
} & InputVariantProps &
  Omit<SlotProps<"input">, "size">

/**
 * Displays a form input field.
 */
const InputBase = forwardRef<HTMLDivElement, InputProps>(function Input(
  {
    asChild,
    children,
    startEnhancer,
    endEnhancer,
    onKeyDown,
    clearable,
    clearOnEscape,
    className,
    style,
    inputRef,
    disabled,
    onClick,
    onMouseDown,
    onEnter,
    value,
    error,
    overrides,
    variant,
    size,
    warning,
    ...inputProps
  },
  ref,
) {
  const localInputRef = useRef<HTMLInputElement>(null)

  const clearValue = () => {
    // trigger a fake input change event (as if all text was deleted)
    const input = localInputRef.current
    if (input) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value",
      )?.set

      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(input, "")
        input.dispatchEvent(
          new Event("input", { bubbles: true, cancelable: true }),
        )
      }
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    localInputRef.current?.focus()
    onClick?.(event)
  }

  const inputRefs = useMemo(
    () => (inputRef ? [localInputRef, inputRef] : [localInputRef]),
    [inputRef],
  )

  const Comp = asChild ? Slot : "input"

  const showClear = clearable && (localInputRef.current?.value || value)

  return (
    <Flex
      className={classNames(
        inputVariants({
          disabled,
          error,
          warning,
          variant,
          leftIcon: Boolean(startEnhancer),
          rightIcon: Boolean(endEnhancer || showClear),
          size,
        }),
        className,
      )}
      ref={ref}
      style={style}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      {...overrides?.Container}
    >
      {startEnhancer ? (
        <FlexCenter className="min-w-fit">{startEnhancer}</FlexCenter>
      ) : null}

      <Comp
        aria-invalid={
          error
            ? "true"
            : overrides?.Container?.["aria-controls"]
              ? undefined
              : "false"
        }
        disabled={disabled}
        ref={useMergedRef(...inputRefs)}
        value={value}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          onKeyDown?.(event)
          if (event.key === "Escape") {
            localInputRef.current?.blur()
            if (clearOnEscape) {
              clearValue()
            }
          }
          if (event.key === "Enter") {
            onEnter?.(value ?? event.currentTarget.value)
          }
        }}
        {...inputProps}
        {...overrides?.Input}
        className={classNames(
          // Input needs to be 16px to prevent input zoom on mobile
          "text-md md:text-sm",
          "w-full border-0 bg-transparent outline-hidden placeholder:text-text-secondary",
          // Hides the "spin"/number picker buttons
          "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          overrides?.Input?.className,
        )}
      >
        {children}
      </Comp>

      {showClear && (
        <VerticalAligned>
          <UnstyledButton onClick={clearValue}>
            <Close
              aria-label="Clear"
              fill="interactive-primary-styles"
              size={20}
            />
          </UnstyledButton>
        </VerticalAligned>
      )}

      {endEnhancer ? (
        <FlexCenter className="min-w-fit">{endEnhancer}</FlexCenter>
      ) : null}
    </Flex>
  )
})

const enhancerSeparatorVariants = tv({
  base: "h-[38px]",
  variants: {
    variant: {
      start: "ml-2",
      end: "mr-2",
    },
  },
})

const EnhancerSeparator = ({
  variant,
}: Required<VariantProps<typeof enhancerSeparatorVariants>>) => (
  <Separator
    className={enhancerSeparatorVariants({ variant })}
    orientation="vertical"
  />
)

export const Input = Object.assign(InputBase, {
  EnhancerSeparator,
})
